<section class="notification-container">
  @let brandingData = (brandingService.brandingData$ | async) || defaultBrandingSettings;
  @if (configService.features[AppFeature.CustomRingtones]) {
    <h1>{{ 'NOTIFICATION_PREFERENCES.MANAGE_CUSTOM_RINGTONES' | translate }}</h1>

    @if (customRingtones.length === 0) {
      <p class="empty-file-list">
        {{ 'NOTIFICATION_PREFERENCES.NO_FILES_MESSAGE' | translate }}
      </p>
    } @else {
      <table class="file-list">
        <thead>
          <tr>
            <th colspan="1"></th>
            <th>{{ 'NOTIFICATION_PREFERENCES.NAME' | translate }}</th>
            <th>{{ 'NOTIFICATION_PREFERENCES.DURATION' | translate }}</th>
            <th>{{ 'NOTIFICATION_PREFERENCES.FILE_SIZE' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (ringtone of customRingtones; track ringtone.id) {
            @let id = ringtone.id;
            <tr>
              <td class="play-file-btn">
                @if (customRingtonePlayingId !== id) {
                  <mat-icon
                    color="primary"
                    (click)="playRingtone(id)">
                    play_circle
                  </mat-icon>
                } @else {
                  <mat-icon
                    color="primary"
                    (click)="pauseRingtone()">
                    stop_circle
                  </mat-icon>
                }
              </td>
              <td>
                @if (!customRingtoneEdit || customRingtoneEdit.id !== id) {
                  <span>{{ ringtone.name }}</span>
                } @else {
                  <span class="inline-file-action-btns">
                    <input [(ngModel)]="customRingtoneEdit.name" />
                    <mat-icon
                      [ngClass]="{ disabled: customRingtoneEdit.name.trim() === '' }"
                      color="primary"
                      title="{{ 'NOTIFICATION_PREFERENCES.SAVE' | translate }}"
                      (click)="handleSaveRingtoneName()"
                      >save</mat-icon
                    >
                    <mat-icon
                      color="primary"
                      title="{{ 'NOTIFICATION_PREFERENCES.CANCEL' | translate }}"
                      (click)="handleCancelRingtoneName()"
                      >cancel</mat-icon
                    >
                  </span>
                }
              </td>
              <td>{{ ringtone.duration | secondsToMinutes }}</td>
              <td>{{ ringtone.blob?.size ?? 0 | fileSize }}</td>
              <td class="file-action-btns">
                <mat-icon
                  color="primary"
                  (click)="downloadCustomRingtone(ringtone)"
                  >download</mat-icon
                >
                <mat-icon
                  color="primary"
                  (click)="editCustomRingtone(ringtone)"
                  >edit</mat-icon
                >
                <mat-icon
                  color="primary"
                  (click)="deleteCustomRingtone(id)"
                  >delete</mat-icon
                >
              </td>
            </tr>
          }
        </tbody>
      </table>
    }

    <div class="upload-button-container">
      @let isSyncing = customRingtonesService.isSyncing$ | async;
      <button
        class="upload-button"
        appBrandingColor
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="uploading || isSyncing"
        [brandingField]="BrandingField.FieldColorFontIconsListAreaItems"
        (click)="openFileSelector()">
        <span>{{ 'NOTIFICATION_PREFERENCES.UPLOAD_FILE' | translate }}</span>
      </button>
      <div class="sync-tooltip">{{ 'NOTIFICATION_PREFERENCES.SYNCING' | translate }}</div>
    </div>

    <input
      type="file"
      #fileInput
      accept=".wav,.mp3,audio/wav,audio/mpeg"
      (change)="addFile($event)"
      [style.display]="'none'" />
  }

  <form [formGroup]="formGroup">
    <div formArrayName="formItems">
      @for (item of formItems.controls; track $index; let i = $index) {
        <div
          class="container"
          [formGroupName]="i">
          <h1 class="m-b-8">{{ notificationItems[i].title }}</h1>
          <div class="flex-col">
            <mat-checkbox
              formControlName="enableSound"
              (ngModelChange)="onCheckChange()"
              color="primary"
              [style.--branding-color]="brandingData[BrandingField.FieldColorFontIconsListAreaItems]">
              {{ 'NOTIFICATION_PREFERENCES.ENABLE_SOUND' | translate: { itemName: getItemName(i) } }}
            </mat-checkbox>
            <mat-checkbox
              formControlName="enableNotification"
              (ngModelChange)="onCheckChange()"
              color="primary"
              [style.--branding-color]="brandingData[BrandingField.FieldColorFontIconsListAreaItems]">
              {{ 'NOTIFICATION_PREFERENCES.SHOW_NOTIFICATION_MINIMIZED' | translate }}
            </mat-checkbox>
            <mat-checkbox
              formControlName="enableForegroundNotification"
              (ngModelChange)="onCheckChange()"
              class="enable-notification"
              color="primary"
              [style.--branding-color]="brandingData[BrandingField.FieldColorFontIconsListAreaItems]">
              {{ 'NOTIFICATION_PREFERENCES.SHOW_NOTIFICATION_FOREGROUND' | translate }}
            </mat-checkbox>
            <mat-form-field
              appearance="outline"
              class="select-dropdown density-minus-3">
              <mat-label>{{ 'NOTIFICATION_PREFERENCES.SELECT_LABEL' | translate }}</mat-label>
              <div class="flex">
                <mat-select
                  formControlName="soundSelect"
                  (selectionChange)="onRingtoneChanged(i, $event.value)"
                  class="device-list">
                  @for (option of notificationItems[i].selectOptions; track $index) {
                    <mat-option [value]="option.ringtone">
                      {{ option.title }}
                    </mat-option>
                  }
                  @if (configService.features[AppFeature.CustomRingtones] && customRingtoneOptions.length > 0) {
                    <mat-optgroup [label]="'NOTIFICATION_PREFERENCES.CUSTOM_RINGTONES_GROUP' | translate">
                      @for (option of customRingtoneOptions; track option.ringtone) {
                        <mat-option [value]="option.ringtone">
                          {{ option.title }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                </mat-select>
                <mat-icon
                  (click)="play(item.value.soundSelect, $event)"
                  class="speaker-icon"
                  appBrandingColor
                  [brandingField]="BrandingField.FieldColorFontIconsListAreaItems"
                  styleType="color"
                  >volume_up</mat-icon
                >
              </div>
            </mat-form-field>
            @if (notificationItems[i].title.includes('Distinctive Ring')) {
              <mat-form-field
                appearance="outline"
                class="select-dropdown density-minus-3">
                <mat-label>{{ 'NOTIFICATION_PREFERENCES.SELECT_DEPARTMENT' | translate }}</mat-label>
                <div class="flex">
                  <mat-select
                    formControlName="departmentSelect"
                    (selectionChange)="onDepartmentChanged(i, $event.value)"
                    class="device-list">
                    @for (option of this.configService.departments$ | async; track $index) {
                      <mat-option [value]="option">
                        {{ option }}
                      </mat-option>
                    }
                  </mat-select>
                </div>
              </mat-form-field>
            }
          </div>
        </div>
        @if (notificationItems[i].title.includes('Voicemail') && sharedVoicemailItems.length > 0) {
          <ng-container [ngTemplateOutlet]="sharedVoicemailSettings"></ng-container>
        }
      }
    </div>
  </form>
</section>

<ng-template #sharedVoicemailSettings>
  <h1 class="m-b-8">{{ 'Shared Voicemail' }}</h1>
  <div [formGroup]="formGroup">
    <div formArrayName="sharedVoicemailItems">
      @for (item of sharedVoicemailFormArray.controls; track $index; let i = $index) {
        <div
          class="container"
          [formGroupName]="i">
          @let name = sharedVoicemailItems[i].mailbox.name;
          @let user = sharedVoicemailItems[i].mailbox.user;
          <h2 class="m-b-8">{{ name ? name + ' - ' + user : user }}</h2>
          <div class="flex-col">
            <mat-checkbox
              formControlName="enableSound"
              (ngModelChange)="onCheckChange()"
              color="primary"
              [style.--branding-color]="brandingData[BrandingField.FieldColorFontIconsListAreaItems]">
              {{ 'NOTIFICATION_PREFERENCES.ENABLE_SOUND' | translate: { itemName: name || user } }}
            </mat-checkbox>
            <mat-checkbox
              formControlName="enableNotification"
              (ngModelChange)="onCheckChange()"
              color="primary"
              [style.--branding-color]="brandingData[BrandingField.FieldColorFontIconsListAreaItems]">
              {{ 'NOTIFICATION_PREFERENCES.SHOW_NOTIFICATION_MINIMIZED' | translate }}
            </mat-checkbox>
            <mat-checkbox
              formControlName="enableForegroundNotification"
              (ngModelChange)="onCheckChange()"
              class="enable-notification"
              color="primary"
              [style.--branding-color]="brandingData[BrandingField.FieldColorFontIconsListAreaItems]">
              {{ 'NOTIFICATION_PREFERENCES.SHOW_NOTIFICATION_FOREGROUND' | translate }}
            </mat-checkbox>
            <mat-form-field
              appearance="outline"
              class="select-dropdown density-minus-3">
              <mat-label>{{ 'NOTIFICATION_PREFERENCES.SELECT_LABEL' | translate }}</mat-label>
              <div class="flex">
                <mat-select
                  formControlName="soundSelect"
                  (selectionChange)="onSharedVoicemailRingtoneChanged(i, $event.value)"
                  class="device-list">
                  @for (option of sharedVoicemailSoundOptions; track $index) {
                    <mat-option [value]="option.ringtone">
                      {{ option.title }}
                    </mat-option>
                  }
                  @if (configService.features[AppFeature.CustomRingtones] && customRingtoneOptions.length > 0) {
                    <mat-optgroup [label]="'NOTIFICATION_PREFERENCES.CUSTOM_RINGTONES_GROUP' | translate">
                      @for (option of customRingtoneOptions; track option.ringtone) {
                        <mat-option [value]="option.ringtone">
                          {{ option.title }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                </mat-select>
                <mat-icon
                  (click)="play(item.value.soundSelect, $event)"
                  class="speaker-icon"
                  appBrandingColor
                  [brandingField]="BrandingField.FieldColorFontIconsListAreaItems"
                  styleType="color"
                  >volume_up</mat-icon
                >
              </div>
            </mat-form-field>
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>
